import { useMutation, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ============================================================================
// Types
// ============================================================================

export interface OnboardingRequest {
  place_id: string
  template_id: number
  custom_data: string
  enable_restaurant_detection: boolean
}

export interface OnboardingResponse {
  task_id: string
}

export interface OnboardingStatus {
  status: 'pending' | 'in_progress' | 'completed' | 'error'
  progress: number
  message: string
  result?: string // URL to the created assistant
}

// ============================================================================
// Query Key Factory
// ============================================================================

export const onboardingKeys = {
  all: ['onboarding'] as const,
  status: (taskId: string) => [...onboardingKeys.all, 'status', taskId] as const,
  data: (taskId: string) => [...onboardingKeys.all, 'data', taskId] as const,
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to start the onboarding process
 * 
 * This starts a Celery task that:
 * 1. Fetches business details from Google Places
 * 2. Analyzes the business website
 * 3. Trains an AI assistant
 * 4. Sets up the account
 */
export function useStartOnboarding() {
  return useMutation({
    mutationFn: async (data: OnboardingRequest): Promise<OnboardingResponse> => {
      const response = await api.post('/onboarding/', data)
      return response.data
    },
  })
}

/**
 * Hook to poll for onboarding task status
 * 
 * Returns the current progress and status of the onboarding task.
 * Polls every 2 seconds while the task is in progress.
 */
export function useOnboardingStatus(taskId: string | null, enabled = true) {
  return useQuery({
    queryKey: onboardingKeys.status(taskId || ''),
    queryFn: async (): Promise<OnboardingStatus> => {
      const response = await api.get(`/onboarding/status/${taskId}`)
      return response.data
    },
    enabled: enabled && !!taskId,
    refetchInterval: (query) => {
      // Stop polling when completed or error
      const status = query.state.data?.status
      if (status === 'completed' || status === 'error') {
        return false
      }
      // Poll every 2 seconds while in progress
      return 2000
    },
    staleTime: 0, // Always fetch fresh data
  })
}

/**
 * Hook to fetch onboarding data by task ID
 * 
 * Retrieves the generated assistant data from an onboarding task.
 * This avoids passing large JSON data in URL query strings.
 * The data is stored in Redis with a 24 hour TTL.
 */
export function useOnboardingData(taskId: string | null) {
  return useQuery({
    queryKey: onboardingKeys.data(taskId || ''),
    queryFn: async () => {
      const response = await api.get(`/onboarding/data/${taskId}`)
      return response.data
    },
    enabled: !!taskId,
    staleTime: Infinity, // Data won't change once generated
  })
}

export default {
  useStartOnboarding,
  useOnboardingStatus,
  useOnboardingData,
}
